import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, saveAuth } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  const goToPortal = (user) => nav(user.role === "admin" ? "/admin" : "/employee");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    // Emergent auth returns to /login#session_id=<id>
    const hash = location.hash || "";
    if (!hash.includes("session_id=") || processed.current) return;
    processed.current = true;
    const session_id = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!session_id) return;
    setProcessing(true);
    // Clean the hash from the URL so it isn't reused
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", { session_id });
        saveAuth(data.token, data.user);
        toast.success(`Welcome, ${data.user.name}`);
        goToPortal(data.user);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Google sign-in failed");
        setProcessing(false);
      }
    })();
  }, [location.hash]);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveAuth(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      goToPortal(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  if (processing) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="text-xs uppercase tracking-widest text-[#CCFF00]">Signing you in</div>
        <div className="font-display text-3xl mt-2">One moment…</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="text-xs uppercase tracking-widest text-[#CCFF00]">Staff Access</div>
      <h1 className="font-display text-4xl mt-2 mb-8">Sign in</h1>

      <button data-testid="google-signin-btn" onClick={startGoogle}
        className="w-full mb-3 flex items-center justify-center gap-3 bg-white text-black font-semibold py-3 rounded-xl hover:bg-zinc-100 transition uppercase tracking-widest text-sm">
        <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4c-7.5 0-13.9 4.1-17.7 10.7z"/>
          <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2c-2 1.4-4.6 2.4-7.3 2.4-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.7 39.6 16.3 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5v.1l6.3 5.2C37.2 41.3 44 36 44 24c0-1.2-.1-2.4-.4-3.5z"/>
        </svg>
        Sign in with Google
      </button>

      <div className="flex items-center gap-3 my-4 text-[10px] uppercase tracking-widest text-zinc-500">
        <div className="flex-1 h-px bg-zinc-800" /> or use password <div className="flex-1 h-px bg-zinc-800" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <input data-testid="login-email" type="email" placeholder="Email" required
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
        <input data-testid="login-password" type="password" placeholder="Password" required
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 rounded-xl border border-zinc-700 focus:border-[#CCFF00] outline-none" />
        <button data-testid="login-submit-btn" disabled={loading} className="neon-btn w-full py-4 rounded-xl">
          {loading ? "..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}
